// components/scans/AddScanCategoryDialog.jsx
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { scanCategoryAPI } from '../../services/api';

const AddScanCategoryDialog = ({ open, onClose, selectedCategory, onSuccess }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);

    const validationSchema = Yup.object({
        name: Yup.string()
            .required(t('scanCategories.nameRequired', 'Category name is required'))
            .min(2, t('scanCategories.nameMinLength', 'Name must be at least 2 characters'))
            .max(50, t('scanCategories.nameMaxLength', 'Name cannot exceed 50 characters')),
        description: Yup.string()
            .max(500, t('scanCategories.descriptionMaxLength', 'Description cannot exceed 500 characters')),
        color: Yup.string()
            .required(t('scanCategories.colorRequired', 'Color is required'))
            .matches(/^#[0-9A-F]{6}$/i, t('scanCategories.colorFormat', 'Color must be a valid hex color'))
    });

    const formik = useFormik({
        initialValues: {
            name: selectedCategory?.name || '',
            description: selectedCategory?.description || '',
            color: selectedCategory?.color || '#1976d2'
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const submitData = {
                    name: values.name.trim(),
                    description: values.description?.trim() || undefined,
                    color: values.color
                };

                if (selectedCategory) {
                    await scanCategoryAPI.update(selectedCategory._id, submitData);
                    toast.success(t('scanCategories.updateSuccess', 'Category updated successfully'));
                } else {
                    await scanCategoryAPI.create(submitData);
                    toast.success(t('scanCategories.createSuccess', 'Category created successfully'));
                }
                onSuccess();
                handleClose();
            } catch (err) {
                console.error('Category save error:', err);
                const errorMessage = err.response?.data?.message || err.message || t('scanCategories.saveError');
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    // Predefined color options
    const colorPresets = [
        '#1976d2', '#2196f3', '#03a9f4', '#00bcd4',
        '#009688', '#4caf50', '#8bc34a', '#cddc39',
        '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
        '#f44336', '#e91e63', '#9c27b0', '#673ab7'
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {selectedCategory
                    ? t('scanCategories.editCategory', 'Edit Category')
                    : t('scanCategories.createNewCategory', 'Create New Category')
                }
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="name"
                                label={t('scanCategories.categoryName', 'Category Name')}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                autoFocus
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="description"
                                label={t('scanCategories.description', 'Description')}
                                multiline
                                rows={3}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={
                                    (formik.touched.description && formik.errors.description) ||
                                    t('scanCategories.optionalDescription', 'Optional description')
                                }
                                placeholder={t('scanCategories.enterOptionalDescription', 'Enter optional description...')}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('scanCategories.color', 'Color')}
                            </Typography>

                            {/* Color Presets */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {colorPresets.map((color) => (
                                    <Box
                                        key={color}
                                        onClick={() => formik.setFieldValue('color', color)}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 1,
                                            backgroundColor: color,
                                            cursor: 'pointer',
                                            border: formik.values.color === color ? '3px solid' : '1px solid',
                                            borderColor: formik.values.color === color ? 'primary.main' : 'divider',
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'scale(1.1)'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Custom Color Picker */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <TextField
                                    name="color"
                                    type="color"
                                    value={formik.values.color}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.color && Boolean(formik.errors.color)}
                                    sx={{ width: 80 }}
                                    InputProps={{
                                        sx: { height: 40, padding: 0.5 }
                                    }}
                                />
                                <TextField
                                    name="color"
                                    size="small"
                                    value={formik.values.color}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.color && Boolean(formik.errors.color)}
                                    helperText={formik.touched.color && formik.errors.color}
                                    placeholder="#1976d2"
                                    sx={{ width: 120 }}
                                    inputProps={{
                                        style: { fontFamily: 'monospace' }
                                    }}
                                />
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        backgroundColor: formik.values.color,
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={loading}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formik.isValid}
                    >
                        {loading ? (
                            <CircularProgress size={20} />
                        ) : selectedCategory ? (
                            t('common.update', 'Update')
                        ) : (
                            t('common.create', 'Create')
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddScanCategoryDialog;